import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Checkbox,
  Divider,
  HStack,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import {
  admissionSectionField,
  AdmissionFluidColor,
  BirthingCenterAdmissionData,
  EncounterType,
  PrenatalExamData,
  ProviderHandoff,
} from '@hb/shared'
import { useAuth } from '@hb/react'
import { useEffect, useMemo, useState } from 'react'
import { useField, useForm } from 'react-final-form'
import { ActionButton, SolidActionButton } from '@components/Buttons'
import { DefaultModal } from '@components/Modals/DefaultModal'
import { PracticeProviderSelect } from '../../../../components/Practices/components/PracticeProviderSelect'
import { FormElement } from '../../../../components/forms/Input'
import { useProfile } from '../../../../contexts/user/ProfileContext'
import { useAssessmentExams } from '../../../../hooks/backend'
import { useGbsConfig } from '../../../../hooks/backend/gbs/useGbsConfig'
import {
  usePracticeProviderField,
  usePracticeProvidersField,
} from '../../../../components/Practices/hooks/usePracticeProviders'
import { useLaborCharting } from '../../../Pregnancy/LaborCharting/context'

const THIRTY_MINUTES_MS = 30 * 60 * 1000

const useAdmittingMidwifeId = (): string | null => {
  const {
    input: { value },
  } = useField<string>('data.admittingMidwife', { subscription: { value: true } })
  return value || null
}

const useAdmittedAt = (): number | null => {
  const {
    input: { value },
  } = useField<number>('data.admittedAt', { subscription: { value: true } })
  return value || null
}

const useMembraneFluidColor = (): AdmissionFluidColor | null => {
  const {
    input: { value },
  } = useField<AdmissionFluidColor | undefined>('data.membraneFluidColor', {
    subscription: { value: true },
  })
  return value ?? null
}

const useGestationalAge = (): number | null => {
  const {
    input: { value },
  } = useField<number | undefined>('data.gestationalAge', { subscription: { value: true } })
  return value ?? null
}

// Supervisory consultation — watches note field and auto-stamps recordedAt on first non-empty value.
const SupervisoryConsultationAutoStamp = () => {
  const form = useForm()
  const {
    input: { value: note },
  } = useField<string | undefined>('data.supervisoryConsultation.note', {
    subscription: { value: true },
  })
  const {
    input: { value: recordedAt },
  } = useField<number | undefined>('data.supervisoryConsultation.recordedAt', {
    subscription: { value: true },
  })

  useEffect(() => {
    if (note && !recordedAt) {
      form.change('data.supervisoryConsultation.recordedAt', Date.now())
    }
  }, [form, note, recordedAt])

  return null
}

// Provider Handoff modal — lets the provider record a handoff event inline.
// PracticeProviderSelect writes into the parent RFF form using transient field names
// (_handoff.outgoingProviderId, _handoff.incomingProviderId). handleRecord reads those
// values directly from form state, so no local useState copy is needed.
const AddHandoffModal = ({
  isOpen,
  onClose,
  practiceId,
}: {
  isOpen: boolean
  onClose: () => void
  practiceId: string | null
}) => {
  const form = useForm()
  const [note, setNote] = useState<string>('')

  // Read transient RFF fields that PracticeProviderSelect writes into.
  const {
    input: { value: outgoingId },
  } = useField<string>('_handoff.outgoingProviderId', { subscription: { value: true } })
  const {
    input: { value: incomingId },
  } = useField<string>('_handoff.incomingProviderId', { subscription: { value: true } })

  // Clear the note and transient fields when modal closes.
  useEffect(() => {
    if (!isOpen) {
      setNote('')
      form.change('_handoff.outgoingProviderId', undefined)
      form.change('_handoff.incomingProviderId', undefined)
    }
  }, [isOpen, form])

  const handleRecord = () => {
    if (!outgoingId || !incomingId) return
    const formValues = form.getState().values as { data: BirthingCenterAdmissionData }
    const newHandoff: ProviderHandoff = {
      id: crypto.randomUUID(),
      outgoingProviderId: outgoingId,
      incomingProviderId: incomingId,
      handoffAt: Date.now(),
      ...(note.trim() ? { handoffNote: note.trim() } : {}),
    }
    form.change('data.providerHandoffs', [...(formValues.data?.providerHandoffs ?? []), newHandoff])
    onClose()
  }

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      header="Add Provider Handoff"
      size="md"
      render={() => (
        <VStack align="stretch" spacing={4} p={4}>
          <Box>
            <Text fontSize="xs" fontWeight={600} color="gray.600" mb={1} textTransform="uppercase">
              Outgoing Provider
            </Text>
            <PracticeProviderSelect
              practiceId={practiceId}
              name="_handoff.outgoingProviderId"
              placeholder="Outgoing Provider"
            />
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight={600} color="gray.600" mb={1} textTransform="uppercase">
              Incoming Provider
            </Text>
            <PracticeProviderSelect
              practiceId={practiceId}
              name="_handoff.incomingProviderId"
              placeholder="Incoming Provider"
            />
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight={600} color="gray.600" mb={1} textTransform="uppercase">
              Handoff Note (optional)
            </Text>
            <Textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Any relevant context for the incoming provider..."
              size="sm"
              rows={3}
            />
          </Box>
        </VStack>
      )}
      renderFooter={({ onClose: closeModal }) => (
        <HStack justify="flex-end" spacing={2}>
          <ActionButton onClick={closeModal}>Cancel</ActionButton>
          <SolidActionButton onClick={handleRecord} isDisabled={!outgoingId || !incomingId}>
            Record Handoff
          </SolidActionButton>
        </HStack>
      )}
    />
  )
}

export const BirthingCenterAdmissionInput = () => {
  const { practiceId } = useLaborCharting()
  const { assessmentId } = useProfile()
  const currentUserId = useAuth(s => s.authUser?.uid)
  const form = useForm()
  const admittingMidwifeId = useAdmittingMidwifeId()
  const admittedAt = useAdmittedAt()
  const fluidColor = useMembraneFluidColor()
  const gestationalAge = useGestationalAge()

  const [handoffModalOpen, setHandoffModalOpen] = useState(false)

  // §3.1: 30-minute lock on admittedAt — field becomes read-only after 30 min from capture.
  const isAdmittedAtLocked = !!admittedAt && Date.now() - admittedAt > THIRTY_MINUTES_MS

  const admittingMidwifeField = usePracticeProviderField(practiceId, 'Admitting Midwife')
  const excludeIds = useMemo(
    () => (admittingMidwifeId ? [admittingMidwifeId] : undefined),
    [admittingMidwifeId],
  )
  const supportPersonsField = usePracticeProvidersField(practiceId, 'Support Persons', excludeIds)
  const additionalProvidersPresentField = usePracticeProvidersField(
    practiceId,
    'Additional Providers Present',
    excludeIds,
  )

  const dynamicField = useMemo(
    () => ({
      ...admissionSectionField,
      children: {
        ...admissionSectionField.children,
        // Lock admittedAt to read-only after 30 minutes from initial capture.
        admittedAt: { ...admissionSectionField.children.admittedAt, readOnly: isAdmittedAtLocked },
        admittingMidwife: admittingMidwifeField,
        supportPersons: supportPersonsField,
        additionalProvidersPresent: additionalProvidersPresentField,
      },
    }),
    [
      admittingMidwifeField,
      supportPersonsField,
      additionalProvidersPresentField,
      isAdmittedAtLocked,
    ],
  )

  // §3.1: GBS status — read from the most recent antepartum encounter in this episode.
  const { dataArr: antepartumEncounters } = useAssessmentExams(
    assessmentId,
    EncounterType.ANTEPARTUM,
  )

  const recentGbsStatus = useMemo(() => {
    for (const enc of antepartumEncounters ?? []) {
      const gbsStatus = (enc.data as PrenatalExamData)?.labs?.gbsStatus
      if (gbsStatus) return gbsStatus
    }
    return null
  }, [antepartumEncounters])

  const antepartumCount = antepartumEncounters?.length ?? 0

  // §3.1.1 + §J: GBS acknowledgment gate — Tier 1 when GA ≥ 35w and GBS is unknown/absent.
  const { gbsGaTriggerDays } = useGbsConfig()
  const gbsIsUnknown = !recentGbsStatus || recentGbsStatus === 'unknown'
  const showGbsAcknowledgmentGate =
    typeof gestationalAge === 'number' && gestationalAge >= gbsGaTriggerDays && gbsIsUnknown

  const {
    input: { value: gbsAcknowledged, onChange: onGbsAcknowledgedChange },
  } = useField<boolean | undefined>('data.gbsStatusAcknowledged', { subscription: { value: true } })

  // §3.2: Meconium alert — fires when fluid color is meconium-thin or meconium-thick.
  const isMeconium = fluidColor === 'meconium-thin' || fluidColor === 'meconium-thick'

  const gaDisplay =
    gestationalAge != null ? `${Math.floor(gestationalAge / 7)}w ${gestationalAge % 7}d` : null

  // Provider handoffs — read from form state for the acknowledge action.
  const {
    input: { value: providerHandoffs },
  } = useField<ProviderHandoff[] | undefined>('data.providerHandoffs', {
    subscription: { value: true },
  })

  const handleAcknowledgeHandoff = (handoffId: string) => {
    if (!currentUserId) return
    const updated = (providerHandoffs ?? []).map(h =>
      h.id === handoffId ? { ...h, acknowledgedAt: Date.now(), acknowledgedBy: currentUserId } : h,
    )
    form.change('data.providerHandoffs', updated)
  }

  return (
    <VStack data-child-name="data" align="stretch" spacing={3} w="100%" px={4} py={3}>
      {/* Headless auto-stamp for supervisory consultation */}
      <SupervisoryConsultationAutoStamp />

      {/* §3.1: GA at Admission — auto-populated by PregnancyHeader in step 1 */}
      {gaDisplay && (
        <HStack
          bg="blue.50"
          borderRadius={6}
          px={3}
          py={2}
          border="1px solid"
          borderColor="blue.100">
          <Text fontSize="xs" fontWeight={600} color="blue.700" textTransform="uppercase">
            GA at Admission
          </Text>
          <Badge colorScheme="blue" fontSize="sm">
            {gaDisplay}
          </Badge>
        </HStack>
      )}

      {/* §3.1: Prenatal care status */}
      {antepartumCount === 0 ? (
        <Alert status="warning" borderRadius={6} fontSize="sm">
          <AlertIcon />
          No prenatal care on record for this pregnancy episode.
        </Alert>
      ) : (
        <HStack
          bg="green.50"
          borderRadius={6}
          px={3}
          py={2}
          border="1px solid"
          borderColor="green.100">
          <Text fontSize="xs" fontWeight={600} color="green.700">
            {antepartumCount} antepartum visit{antepartumCount !== 1 ? 's' : ''} on record
          </Text>
        </HStack>
      )}

      {/* §3.1: GBS status — display-only from prenatal chart */}
      {recentGbsStatus && (
        <Box>
          <HStack
            bg="gray.50"
            borderRadius={6}
            px={3}
            py={2}
            border="1px solid"
            borderColor="gray.200">
            <Text fontSize="xs" fontWeight={600} color="gray.600" textTransform="uppercase">
              GBS Status (from prenatal chart)
            </Text>
            <Badge
              colorScheme={
                recentGbsStatus === 'positive'
                  ? 'red'
                  : recentGbsStatus === 'negative'
                    ? 'green'
                    : 'gray'
              }
              fontSize="xs">
              {recentGbsStatus.charAt(0).toUpperCase() + recentGbsStatus.slice(1)}
            </Badge>
          </HStack>
          {recentGbsStatus === 'positive' && (
            <Alert status="warning" mt={2} borderRadius="md" fontSize="sm">
              <AlertIcon />
              GBS positive — intrapartum antibiotic prophylaxis indicated. Verify antibiotic
              protocol is in place.
            </Alert>
          )}
        </Box>
      )}

      {/* §3.1.1 + §J (Addendum June 2026): GBS acknowledgment gate
          Tier 1 — provider must acknowledge and document a management plan
          before the admission note can be authenticated.
          Fires when GA ≥ 35w (gbsGaTriggerDays) and GBS status is unknown/absent. */}
      {showGbsAcknowledgmentGate && (
        <Box
          borderRadius={6}
          border="1px solid"
          borderColor="orange.300"
          bg="orange.50"
          _dark={{ bg: 'orange.900', borderColor: 'orange.600' }}
          p={3}>
          <Alert status="warning" borderRadius={4} fontSize="sm" mb={2}>
            <AlertIcon />
            GBS status unknown at ≥35w — document GBS status and management plan before
            authenticating.
          </Alert>
          <Checkbox
            isChecked={!!gbsAcknowledged}
            onChange={e => onGbsAcknowledgedChange(e.target.checked || undefined)}
            colorScheme="orange"
            size="sm">
            <Text fontSize="sm" color="gray.700" _dark={{ color: 'gray.200' }}>
              I have reviewed GBS status and documented a management plan.
            </Text>
          </Checkbox>
        </Box>
      )}

      {/* §3.1: 30-minute lock notice */}
      {isAdmittedAtLocked && (
        <Alert status="warning" borderRadius={6} fontSize="sm">
          <AlertIcon />
          Admission time is locked (more than 30 minutes since capture). Use the note amendment
          workflow to correct the admission time.
        </Alert>
      )}

      <Divider />

      <FormElement root showName={false} field={dynamicField} name="data" />

      {/* §3.2: Meconium neonatal alert */}
      {isMeconium && (
        <Alert status="error" borderRadius={6} fontSize="sm">
          <AlertIcon />
          Meconium-stained fluid — neonatal resuscitation team notification indicated.
        </Alert>
      )}

      <Divider />

      {/* Provider Handoffs — list of recorded handoffs with acknowledge action */}
      {(providerHandoffs ?? []).length > 0 && (
        <VStack align="stretch" spacing={2}>
          <Text fontSize="xs" fontWeight={600} color="gray.600" textTransform="uppercase">
            Provider Handoffs
          </Text>
          {(providerHandoffs ?? []).map(handoff => (
            <Box
              key={handoff.id}
              borderRadius={6}
              border="1px solid"
              borderColor="gray.200"
              bg="gray.50"
              p={3}>
              <HStack justify="space-between" align="flex-start">
                <VStack align="stretch" spacing={0}>
                  <Text fontSize="xs" color="gray.500">
                    {new Date(handoff.handoffAt).toLocaleString()}
                  </Text>
                  <Text fontSize="sm">
                    <Text as="span" fontWeight={600}>
                      Out:
                    </Text>{' '}
                    {handoff.outgoingProviderId}
                  </Text>
                  <Text fontSize="sm">
                    <Text as="span" fontWeight={600}>
                      In:
                    </Text>{' '}
                    {handoff.incomingProviderId}
                  </Text>
                  {handoff.handoffNote && (
                    <Text fontSize="sm" color="gray.600" mt={1}>
                      {handoff.handoffNote}
                    </Text>
                  )}
                </VStack>
                <VStack align="flex-end" spacing={1}>
                  {handoff.acknowledgedAt ? (
                    <Badge colorScheme="green" fontSize="xs">
                      Acknowledged {new Date(handoff.acknowledgedAt).toLocaleString()}
                    </Badge>
                  ) : (
                    <>
                      <Badge colorScheme="gray" fontSize="xs">
                        Pending acknowledgment
                      </Badge>
                      <ActionButton size="xs" onClick={() => handleAcknowledgeHandoff(handoff.id)}>
                        Acknowledge
                      </ActionButton>
                    </>
                  )}
                </VStack>
              </HStack>
            </Box>
          ))}
        </VStack>
      )}

      <ActionButton alignSelf="flex-start" size="sm" onClick={() => setHandoffModalOpen(true)}>
        + Add Provider Handoff
      </ActionButton>

      <AddHandoffModal
        isOpen={handoffModalOpen}
        onClose={() => setHandoffModalOpen(false)}
        practiceId={practiceId}
      />
    </VStack>
  )
}
